import React, { useState, useEffect } from 'react';
import { distributionListsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Mail, Users, Clock, Edit, Trash2, FileText } from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import DistributionListModal from './DistributionListModal';
import EmailHistorySection from './EmailHistorySection';

const TEMPLATE_LABELS = {
  standard: 'Standard',
  executive: 'Executive',
  compact: 'Compact',
  visual: 'Visual'
};

const DistributionListsTab = ({ project, teamMembers = [] }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadLists();
  }, [project.id]);

  const loadLists = async () => {
    try {
      const response = await distributionListsAPI.list(project.id);
      setLists(response.data);
    } catch (error) {
      console.error('Failed to load distribution lists:', error);
      toast.error('Failed to load distribution lists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingList(null);
    setShowModal(true);
  };

  const handleEdit = (list) => {
    setEditingList(list);
    setShowModal(true);
  };

  const handleDelete = async (listId) => {
    try {
      await distributionListsAPI.delete(project.id, listId);
      setLists(lists.filter(l => l.id !== listId));
      toast.success('Distribution list deleted');
    } catch (error) {
      console.error('Failed to delete list:', error);
      toast.error('Failed to delete distribution list');
    }
    setDeleteConfirm(null);
  };

  const handleSave = async (listData) => {
    try {
      if (editingList) {
        const response = await distributionListsAPI.update(project.id, editingList.id, listData);
        setLists(lists.map(l => l.id === editingList.id ? response.data : l));
        toast.success('Distribution list updated');
      } else {
        const response = await distributionListsAPI.create(project.id, listData);
        setLists([...lists, response.data]);
        toast.success('Distribution list created');
      }
      setShowModal(false);
      setEditingList(null);
    } catch (error) {
      console.error('Failed to save list:', error);
      toast.error('Failed to save distribution list');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="distribution-lists-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Distribution Lists</h2>
          <p className="text-sm text-slate-500">Manage email recipients for status reports</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-distribution-list-btn">
          <Plus className="h-4 w-4 mr-2" />
          New List
        </Button>
      </div>

      {lists.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Mail className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">No Distribution Lists Yet</h3>
            <p className="text-sm text-slate-500 mb-4">
              Create distribution lists to easily email status reports to stakeholders.
            </p>
            <Button onClick={handleCreate} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Create Distribution List
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {lists.map((list) => (
            <Card key={list.id} data-testid={`distribution-list-${list.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{list.name}</CardTitle>
                    {list.description && (
                      <CardDescription className="mt-1">{list.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => handleEdit(list)}
                      data-testid={`edit-list-${list.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteConfirm(list)}
                      data-testid={`delete-list-${list.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <Users className="h-4 w-4" />
                    <span>{list.recipients?.length || 0} recipients</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <FileText className="h-4 w-4" />
                    <span>{TEMPLATE_LABELS[list.default_template] || 'Standard'} template</span>
                  </div>
                  {list.last_sent_at && (
                    <div className="flex items-center gap-1.5 text-slate-500">
                      <Clock className="h-4 w-4" />
                      <span>Last sent {formatDate(list.last_sent_at)}</span>
                    </div>
                  )}
                </div>
                {list.recipients?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {list.recipients.slice(0, 5).map((r, i) => (
                      <Badge key={i} variant="secondary" className="font-normal">
                        {r.name || r.email}
                      </Badge>
                    ))}
                    {list.recipients.length > 5 && (
                      <Badge variant="outline" className="font-normal">
                        +{list.recipients.length - 5} more
                      </Badge>
                    )}
                  </div>
                )}
                
                {/* Email History Section */}
                <EmailHistorySection 
                  projectId={project.id} 
                  listId={list.id} 
                  listName={list.name}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <DistributionListModal
          open={showModal}
          onOpenChange={setShowModal}
          list={editingList}
          onSave={handleSave}
          teamMembers={teamMembers}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Distribution List</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteConfirm.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DistributionListsTab;
